import React, { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Drawer, useDrawer, drawerBackdropId } from '@frontend/design-system';
import { useSettingsPanelStore, useSettingsPanelShallowStore } from './store';

const drawerZIndex = theme.zIndex.modals;

export const SettingsPanelLayout: React.FC = React.memo(() => {
  const { updateAvailableWidth, hasTrayPanels, heightOffset } = useSettingsPanelShallowStore(
    'updateAvailableWidth',
    'hasTrayPanels',
    'heightOffset'
  );

  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const { modalProps } = useDrawer({
    open: hasTrayPanels,
    onOpenChange: (open) => {
      if (!open) {
        const trayIds = useSettingsPanelStore.getState().trayIds;
        const panelCloseFunctions = useSettingsPanelStore.getState().panelCloseFunctions;
        trayIds.forEach((id) => panelCloseFunctions[id]?.());
      }
    },
    outsidePress: (event) =>
      (event.target as HTMLElement).id === drawerBackdropId ||
      !!(event.target as HTMLElement).closest('#settings-top-bar'),
  });

  const resizeRafId = useRef<number | null>(null);

  useEffect(() => {
    function onResize() {
      if (resizeRafId.current) {
        cancelAnimationFrame(resizeRafId.current);
      }
      resizeRafId.current = requestAnimationFrame(() => {
        updateAvailableWidth(window.innerWidth);
      });
    }

    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (resizeRafId.current) cancelAnimationFrame(resizeRafId.current);
    };
  }, [updateAvailableWidth]);

  return (
    <motion.div
      css={css`
        height: 100%;
      `}
    >
      <div
        id='settings-panel-root'
        css={css`
          display: flex;
          height: 100%;
          isolation: isolate;
        `}
      />
      <Drawer
        keepMounted
        additionalHeightOffset={isXSmall ? 64 + heightOffset : 0}
        css={[
          css`
            display: flex;
            padding: 0;
            max-width: 100%;
          `,
          isXSmall
            ? css`
                width: 100%;
              `
            : css`
                width: auto;
              `,
        ]}
        {...modalProps}
        size={isXSmall ? 'large' : 'auto'}
        zIndex={drawerZIndex}
      >
        <motion.div
          css={css`
            display: flex;
            height: 100%;
          `}
          id='settings-tray-root'
        />
      </Drawer>
    </motion.div>
  );
});

SettingsPanelLayout.displayName = 'SettingsPanelLayout';
